import React, { useState, useEffect } from 'react';
import { X, Users, CheckCircle, Clock, AlertCircle, Eye, Plus, Send, Calendar, Edit2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { questionnaireService } from '../services/questionnaireService';
import { adminNotificationService } from '../services/adminNotificationService';
import AssignQuestionnaireModal from './AssignQuestionnaireModal';
import QuestionnaireResponseView from './QuestionnaireResponseView';
import type { Questionnaire, QuestionnaireAssignment } from '../types/questionnaires';

interface QuestionnaireAssignmentsViewProps {
  questionnaire: Questionnaire;
  onClose: () => void;
}

const QuestionnaireAssignmentsView: React.FC<QuestionnaireAssignmentsViewProps> = ({ 
  questionnaire, 
  onClose 
}) => {
  const { userProfile } = useAuth();
  const [assignments, setAssignments] = useState<QuestionnaireAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<QuestionnaireAssignment | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [editingDeadlineId, setEditingDeadlineId] = useState<string | null>(null);
  const [newDeadline, setNewDeadline] = useState<string>('');

  useEffect(() => {
    loadAssignments();
  }, [questionnaire.id]);

  const loadAssignments = async () => {
    try {
      setIsLoading(true);
      const data = await questionnaireService.getAssignmentsByQuestionnaire(questionnaire.id);
      setAssignments(data);
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = async (assignment: QuestionnaireAssignment) => {
    if (!userProfile) return;
    
    const confirmed = window.confirm(
      `E-Mail-Einladung erneut an ${assignment.assignedTo} senden?\n\n` +
      `Eine neue Einladung mit dem Link zum Fragebogen wird versendet.`
    );
    
    if (!confirmed) return;
    
    try {
      setResendingId(assignment.id);
      
      const success = await adminNotificationService.sendQuestionnaireInvitation({
        recipientEmail: assignment.assignedTo,
        recipientName: assignment.assignedTo.split('@')[0],
        questionnaireTitle: assignment.questionnaireTitle,
        templateName: assignment.templateName,
        deadline: assignment.deadline,
        assignedByName: userProfile.displayName,
        assignmentId: assignment.id
      });
      
      if (success) {
        alert('✅ E-Mail erfolgreich erneut versendet!');
      } else {
        alert('⚠️ E-Mail konnte nicht versendet werden. Bitte überprüfen Sie die EmailJS-Konfiguration.');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Fehler beim Versenden der E-Mail. Bitte versuchen Sie es erneut.');
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelAssignment = async (assignment: QuestionnaireAssignment) => {
    if (!userProfile) return;

    const reason = prompt('Grund für Stornierung:');
    if (!reason || !reason.trim()) return;

    try {
      await questionnaireService.cancelAssignment(
        assignment.id,
        reason,
        userProfile.id,
        userProfile.displayName
      );
      alert('Zuweisung storniert');
      loadAssignments();
    } catch (error) {
      console.error('Error:', error);
      alert('Fehler beim Stornieren');
    }
  };

  const handleStartEditDeadline = (assignment: QuestionnaireAssignment) => {
    setEditingDeadlineId(assignment.id);
    // Format date as YYYY-MM-DD for input
    const deadline = new Date(assignment.deadline);
    const formattedDate = deadline.toISOString().split('T')[0];
    setNewDeadline(formattedDate);
  };

  const handleCancelEditDeadline = () => {
    setEditingDeadlineId(null);
    setNewDeadline('');
  };

  const handleSaveDeadline = async (assignmentId: string) => {
    if (!newDeadline) return;

    try {
      const newDeadlineDate = new Date(newDeadline);
      await questionnaireService.updateAssignment(assignmentId, {
        deadline: newDeadlineDate
      });
      alert('✅ Deadline erfolgreich aktualisiert!');
      setEditingDeadlineId(null);
      setNewDeadline('');
      loadAssignments();
    } catch (error) {
      console.error('Error updating deadline:', error);
      alert('Fehler beim Aktualisieren der Deadline');
    }
  };

  const getStatusInfo = (assignment: QuestionnaireAssignment) => {
    const now = new Date();
    const isPastDeadline = new Date(assignment.deadline) < now;
    
    if (assignment.status === 'cancelled') {
      return {
        label: 'Storniert',
        color: 'bg-red-100 text-red-800',
        icon: AlertCircle
      };
    }
    
    if (assignment.status === 'submitted') {
      return { 
        label: 'Abgeschickt', 
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle 
      };
    }
    
    if (isPastDeadline) {
      return { 
        label: 'Deadline überschritten', 
        color: 'bg-red-100 text-red-800',
        icon: AlertCircle 
      };
    }
    
    if (assignment.status === 'in_progress') {
      return { 
        label: 'In Bearbeitung', 
        color: 'bg-blue-100 text-blue-800',
        icon: Clock 
      };
    }
    
    return { 
      label: 'Zugewiesen', 
      color: 'bg-gray-100 text-gray-800',
      icon: Users 
    };
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: assignments.length,
    submitted: assignments.filter(a => a.status === 'submitted').length,
    pending: assignments.filter(a => a.status !== 'submitted').length
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Zuweisungen</h2>
              <p className="text-gray-600">{questionnaire.title}</p>
              
              {/* Stats */}
              <div className="flex gap-4 mt-3 text-sm">
                <span className="text-gray-600">
                  Gesamt: <span className="font-semibold">{stats.total}</span>
                </span>
                <span className="text-green-600">
                  Abgeschickt: <span className="font-semibold">{stats.submitted}</span>
                </span>
                <span className="text-blue-600">
                  Ausstehend: <span className="font-semibold">{stats.pending}</span>
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Assign Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowAssignModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Neue Zuweisung
            </button>
          </div>

          {/* Assignments List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p>Noch keine Zuweisungen</p>
              <p className="text-sm">Klicken Sie auf "Neue Zuweisung", um den Fragebogen zu versenden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assignments.map(assignment => {
                const statusInfo = getStatusInfo(assignment);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div 
                    key={assignment.id}
                    className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow ${assignment.status === 'cancelled' ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{assignment.assignedTo}</h4>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          {/* Deadline - Editable for non-cancelled assignments */}
                          {editingDeadlineId === assignment.id ? (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <input
                                type="date"
                                value={newDeadline}
                                onChange={(e) => setNewDeadline(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              />
                              <button
                                onClick={() => handleSaveDeadline(assignment.id)}
                                className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition-colors"
                              >
                                Speichern
                              </button>
                              <button
                                onClick={handleCancelEditDeadline}
                                className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-xs hover:bg-gray-400 transition-colors"
                              >
                                Abbrechen
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>Deadline: {formatDate(assignment.deadline)}</span>
                              {assignment.status !== 'cancelled' && assignment.status !== 'submitted' && (
                                <button
                                  onClick={() => handleStartEditDeadline(assignment)}
                                  className="text-purple-600 hover:text-purple-700 transition-colors"
                                  title="Deadline bearbeiten"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                          {assignment.submittedAt && (
                            <div className="flex items-center text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              <span>Abgeschickt: {formatDate(assignment.submittedAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4 flex flex-col gap-2">
                        {/* View response button (if submitted) */}
                        {assignment.status === 'submitted' && (
                          <button
                            onClick={() => setSelectedAssignment(assignment)}
                            className="px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            Antwort ansehen
                          </button>
                        )}
                        
                        {/* Cancel assignment button (only if not submitted or cancelled) */}
                        {assignment.status !== 'submitted' && assignment.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelAssignment(assignment)}
                            className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                          >
                            Stornieren
                          </button>
                        )}
                        
                        {/* Re-send email button (only if not cancelled) */}
                        {assignment.status !== 'cancelled' && (
                          <button
                            onClick={() => handleResendInvitation(assignment)}
                            disabled={resendingId === assignment.id}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="E-Mail-Einladung erneut senden"
                          >
                            <Send className="w-4 h-4" />
                            {resendingId === assignment.id ? 'Sendet...' : 'Erneut senden'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {showAssignModal && (
        <AssignQuestionnaireModal
          questionnaire={questionnaire}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => {
            setShowAssignModal(false);
            loadAssignments();
          }}
        />
      )}
      
      {selectedAssignment && (
        <QuestionnaireResponseView
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </div>
  );
};

export default QuestionnaireAssignmentsView;

